import { API_BASE_URL } from "../config/api";

// ─── In-memory cache ────────────────────────────────────────────────────────
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const cache = new Map(); // key → { data, expiresAt }

const getCached = (key) => {
  const entry = cache.get(key);
  if (entry && Date.now() < entry.expiresAt) return entry.data;
  cache.delete(key);
  return null;
};

const setCached = (key, data) => {
  cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
};

// Call this after a write operation to bust stale data for a given prefix
export const bustCache = (prefix) => {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
};
// ─────────────────────────────────────────────────────────────────────────────

// Generic API fetch helper (with cache)
// Note: the API always returns all languages in each response — no Accept-Language needed
const fetchAPI = async (endpoint) => {
  const cached = getCached(endpoint);
  if (cached !== null) return cached;

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`);

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    setCached(endpoint, data);
    return data;
  } catch (error) {
    console.error(`Failed to fetch ${endpoint}:`, error);
    throw error;
  }
};

// Home Section API
export const getHomeSection = async () => {
  return fetchAPI("/home-sections");
};

// About API
export const getAbout = async () => {
  const data = await fetchAPI("/about");
  return Array.isArray(data) ? data[0] : data;
};

// Contact Data API
export const getContactData = async () => {
  const data = await fetchAPI("/contact-data");
  return Array.isArray(data) ? data[0] : data;
};

// Apartments API
export const getApartments = async () => {
  const response = await fetchAPI("/apartments/paginate?take=50");
  const data = Array.isArray(response?.data) ? response.data : [];
  const seen = new Set();
  return data.filter((apartment) => {
    if (apartment?.isHidden) return false;
    if (seen.has(apartment.id)) return false;
    seen.add(apartment.id);
    return true;
  });
};

export const getApartment = async (id) => {
  const apartment = await fetchAPI(`/apartments/${id}`);
  if (apartment?.isHidden) return null;
  return apartment;
};

// Testimonials API
export const getTestimonials = async () => {
  return fetchAPI("/testimonies");
};

// Amenities API
export const getAmenities = async () => {
  return fetchAPI("/amenity");
};

export const getAmenitySections = async () => {
  return fetchAPI("/amenity-section");
};

// Videos API
export const getVideos = async () => {
  return fetchAPI("/video");
};

// General Images API
export const getGeneralImages = async (category) => {
  return fetchAPI(`/general-images?category=${category}`);
};

// Price List API
export const getPriceListRows = async () => {
  return fetchAPI("/price-list");
};

export const getPriceListNotes = async () => {
  return fetchAPI("/price-list/notes/all");
};
